import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Preview/development only — never true on production domains.
const IS_DEV_PREVIEW =
  typeof window !== "undefined" &&
  (window.location.hostname.endsWith(".preview.emergentagent.com") ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext({
  user: null, membership: null, loading: true,
  login: async () => {}, register: async () => {}, logout: async () => {}, refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [devSession, setDevSession] = useState(false);

  const applyPayload = useCallback((data) => {
    setUser(data?.user || null);
    setMembership(data?.membership || null);
  }, []);

  const tryDevLogin = useCallback(async () => {
    if (!IS_DEV_PREVIEW) return false;
    try {
      const { data } = await axios.post(`${API}/api/auth/dev-login`, {}, { withCredentials: true });
      applyPayload(data);
      setDevSession(true);
      return true;
    } catch (_) {
      return false;
    }
  }, [applyPayload]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      applyPayload(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Access token missing/expired — try the refresh token once before
        // giving up. Prevents phantom logouts mid-onboarding on mobile Safari.
        try {
          await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
          applyPayload(data);
        } catch (_) {
          // Preview/dev only: fall back to an automatic test session.
          const ok = await tryDevLogin();
          if (!ok) applyPayload(null);
        }
      }
      // Network/transient errors: keep the current session state untouched.
    } finally {
      setLoading(false);
    }
  }, [applyPayload, tryDevLogin]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    applyPayload(data);
    return data;
  }, [applyPayload]);

  const register = useCallback(async (name, email, password, foundersDisplay) => {
    const { data } = await axios.post(
      `${API}/api/auth/register`,
      { name, email, password, founders_wall_display: foundersDisplay || null },
      { withCredentials: true }
    );
    applyPayload(data);
    return data;
  }, [applyPayload]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch (_) {}
    applyPayload(null);
    setDevSession(false);
  }, [applyPayload]);

  const value = useMemo(
    () => ({ user, membership, loading, login, register, logout, refresh }),
    [user, membership, loading, login, register, logout, refresh]
  );
  return (
    <AuthContext.Provider value={value}>
      {children}
      {IS_DEV_PREVIEW && user && (
        <div
          data-testid="test-mode-badge"
          className="fixed left-1/2 -translate-x-1/2 z-[200] px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase bg-black/80 border border-gold/50 text-gold/90 rounded-full pointer-events-none select-none whitespace-nowrap max-w-[92vw] overflow-hidden text-ellipsis"
          style={{ top: "calc(0.375rem + env(safe-area-inset-top))" }}
        >
          Test Mode · {user.email}
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
